/**
 * English message catalog — the SINGLE SOURCE OF TRUTH for all user-facing text.
 *
 * Every label, title, placeholder, button, message, error and piece of static copy in the app
 * lives here (never hardcoded in components). To add a language, create a sibling file (e.g.
 * `fr.ts`) that satisfies the `Messages` type and register it in `../index.ts` — no component
 * changes needed.
 *
 * Conventions:
 * - Group keys by screen / feature namespace (`login`, `dashboard`, …).
 * - Rich text with one emphasised span uses `{ text, emphasis }`; the component bolds the
 *   `emphasis` substring inside `text` (translators keep the substring matching).
 */
export const en = {
  // Cross-cutting labels shared by data mappers (member-name conventions), not tied to one screen.
  common: {
    // Shown instead of a guest member's wire name — the backend can echo the owner account's
    // profile name onto a guest row (mobile shows "Guest" too).
    guest: 'Guest',
    // Blocks adding an expense to a group where the signed-in user's role is view-only — shown
    // from the group page's Add-expense button, the New Bill group pick, and the Save guard.
    viewOnlyPermission:
      'You do not have permission for this. Please ask a contributor in this group.',
  },

  login: {
    pageHeading: 'Sign in to Riloma',
    brand: 'Riloma',
    tabs: {
      password: 'Password Login',
      qr: 'QR Login',
    },
    identifier: {
      label: 'Email or Phone Number',
      placeholder: 'Enter Email / Phone Number',
      phonePlaceholder: 'Phone number',
    },
    password: {
      label: 'Password',
      placeholder: 'Enter Password',
      show: 'Show password',
      hide: 'Hide password',
    },
    forgotPassword: 'Forgot Password?',
    submit: 'Login',
    submitting: 'Signing in…',
    divider: 'Or',
    social: {
      loginWith: 'Login with',
      google: 'Google',
      apple: 'Apple',
    },
    terms: {
      prefix: 'By signing up, you agree to Riloma’s',
      terms: 'Terms',
      conjunction: 'and',
      privacy: 'Privacy Policy',
    },
    country: {
      label: 'Country calling code',
      title: 'Select country code',
      searchPlaceholder: 'Search country or code',
      empty: 'No countries found.',
    },
    qr: {
      scanTitle: 'Scan to Login',
      codeTitle: 'Scan to log in to Riloma',
      generating: 'Generating QR code',
      stepsTitle: 'Steps to Login',
      stepsLabel: 'Steps to log in with QR',
      steps: [
        { text: 'Open Riloma on your phone', emphasis: '' },
        {
          text: 'Click on your profile picture and select Web Login by QR',
          emphasis: 'Web Login by QR',
        },
        { text: 'Scan the code shown here', emphasis: '' },
      ],
      scanned: 'Scanned. Signing you in…',
      redeeming: 'Signing in…',
      denied: 'Login request was declined.',
      expired: 'QR code expired.',
      paused: 'Too many refreshes. Please reload the page.',
      featureUnavailable: 'QR login is currently unavailable.',
      reloadCode: 'Generate new code',
      error: 'Something went wrong. Please try again.',
    },
    otp: {
      title: 'Verify Your Account',
      description: 'Your account requires a password reset. We sent a 4-digit verification code to',
      verify: 'Verify OTP',
      verifying: 'Verifying…',
      resend: 'Resend Code',
      resendIn: 'Resend code in',
      backToLogin: 'Back to Login',
      seconds: 's',
      errors: {
        required: 'Please enter the complete 4-digit verification code.',
        invalid: 'Invalid or expired verification code. Please try again.',
        network: 'Unable to verify code due to a network error.',
      },
    },
    validation: {
      identifierRequired: 'Email or phone number is required',
      phoneInvalid: 'Enter a valid phone number',
      emailInvalid: 'Enter a valid email address',
      passwordRequired: 'Password is required',
      passwordMin: 'Use at least 8 characters',
    },
    errors: {
      generic: 'Something went wrong while signing in. Please try again.',
      network: 'Cannot reach the server. Check your connection and try again.',
      // Shown after the `_authenticated` route guard signs a user out for a lapsed/cancelled Pro
      // subscription (web access is Pro-only) — carried across the redirect via ?reason=pro_required.
      proRequired: 'Your Pro subscription has ended. Please upgrade to keep using the web app.',
    },
  },

  // App shell — header + side-menu navigation shared across all authenticated/internal pages.
  shell: {
    brand: 'Riloma',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    search: {
      label: 'Search',
      placeholder: 'Search group name, member and type',
    },
    addExpense: 'Add expense',
    // Shown instead of opening the New Bill modal when the current group is closed (mobile parity).
    closedGroupMessage: 'This group is closed, so nothing can be changed. Reopen it to continue.',
    account: {
      menuLabel: 'Account menu',
      profile: 'Profile',
      settings: 'Settings',
      signOut: 'Sign out',
      logout: 'Logout',
      toDark: 'Switch to dark mode',
      toLight: 'Switch to light mode',
      email: 'Email',
      phone: 'Phone',
      defaultCurrency: 'Default currency',
      timeZone: 'Time zone',
    },
    nav: {
      label: 'Main navigation',
      home: 'Home',
      needsAttention: 'Needs your attention',
      pendingSettlements: 'Pending settlements',
      recentActivities: 'Recent group activities',
      auditTrail: 'Your audit trail',
      help: 'Help & Support',
    },
  },

  // "Needs your attention" / Action-needed page.
  needsAttention: {
    title: 'Action needed',
    back: 'Back',
    tabs: {
      all: 'All',
      incomplete: 'Incomplete',
      noGroup: 'No group',
    },
    showArchived: 'Show only archived',
    columns: {
      date: 'Date',
      description: 'Description',
      amount: 'Amount',
      group: 'Group',
    },
    status: {
      incomplete: 'Incomplete',
      noGroup: 'No group',
    },
    updateDetails: 'Update Details',
    loading: 'Loading…',
    empty: 'No Action Available',
    note: {
      text: 'Pending or incomplete entries older than 30 days are automatically archived. You can find them here anytime to review, fix, or update.',
      emphasis: '30 days',
    },
  },

  // Pending settlements page.
  pendingSettlements: {
    title: 'Pending settlements',
    back: 'Back',
    heading: 'Keep your pending balances settled',
    description:
      'Review and record payments for outstanding balances across your groups. Settling an entry updates balances and marks the settlement as paid.',
    youPay: 'You need to pay',
    youReceive: 'You will receive from',
    tapToRecord: 'Tap to record',
    empty: 'No pending settlements.',
  },

  // "Record Payment" modal — opened from a pending-settlement row ("tap to record").
  recordPayment: {
    title: 'Record Payment',
    close: 'Close',
    sender: 'SENDER',
    receiver: 'RECEIVER',
    disclaimer:
      'This does not move money. It records that you paid outside the app, keeping your shared balances up to date.',
    paymentAmount: 'Payment Amount',
    paidWhen: 'Paid when',
    note: 'Note (optional)',
    notePlaceholder: 'Add a note…',
    markAsPaid: 'Mark as paid',
    today: 'Today',
    amountRequired: 'Amount must be greater than 0.',
    amountExceedsBalance: 'Amount exceeds the balance.',
  },

  // Recent activity page (AG Grid table) — the user's activity feed.
  recentActivity: {
    title: 'Recent activity',
    back: 'Back',
    columns: {
      dateTime: 'Date and time',
      action: 'Action',
      group: 'Group',
      actionBy: 'Action by',
      type: 'Type',
    },
    viewChanges: 'View changes',
    // "Type" labels, keyed by normalized action_type.
    types: {
      created: 'Created',
      updated: 'Updated',
      deleted: 'Deleted',
      closed: 'Closed',
      reopened: 'Reopened',
      restored: 'Restored',
      archived: 'Archived',
      added: 'Added',
      removed: 'Removed',
      enabled: 'Enabled',
      set: 'Set',
      other: 'Activity',
    },
    // "View changes" dialog.
    changes: {
      title: 'Here’s a summary of what was updated in this group.',
      groupPrefix: 'Group:',
      by: 'By',
      before: 'Before',
      after: 'After',
      close: 'Close',
    },
    empty: 'No Recent activity Available',
  },

  // Your Audit Trail page (AG Grid table).
  auditTrail: {
    title: 'Your Audit Trail',
    back: 'Back',
    banner: {
      text: 'Spotted an unfamiliar login or device? Contact Support immediately.',
      emphasis: 'Contact Support',
    },
    columns: {
      time: 'Activity Time',
      device: 'Device',
      deviceBrowser: 'Device & Browser',
      location: 'Approx. Location',
      ip: 'IP address',
    },
    empty: 'No login activity yet.',
  },

  // Help & Support page.
  helpSupport: {
    title: 'Help and Support',
    back: 'Back',
    heading: 'Choose how you want to reach us',
    description: 'Pick the option that fits best. Most requests take only a minute.',
    options: {
      reportProblem: {
        title: 'Report a problem',
        description: 'Something is not working as expected',
      },
      suggest: {
        title: 'Suggest an improvement',
        description: 'Share an idea to make Riloma better',
      },
      feedback: {
        title: 'Share feedback',
        description: 'Tell us about your experience',
      },
    },
    email: {
      title: 'Prefer email?',
      before: 'Write to',
      address: 'askcs@riloma.com',
      after: 'with a short note and a screenshot if possible.',
    },
    modals: {
      reportProblem: {
        title: 'Report a problem',
        subtitle: 'Something is not working as expected',
        topic: 'Topic',
        topics: {
          login: 'Login',
          expenses: 'Expenses',
          balances: 'Balances',
          receipts: 'Receipts',
          billing: 'Billing',
          privacy: 'Privacy',
        },
        issueTitle: 'Issue title',
        issueTitlePlaceholder: 'Example: Button does not work',
        steps: 'Steps you followed',
        stepsPlaceholder: 'I opened the app, tapped the button, and nothing happened.',
        expected: 'What you expected',
        expectedPlaceholder: 'Example: It should open the next screen',
        actual: 'What happened',
        actualPlaceholder: 'Example: Nothing happened',
        action: 'Send report',
      },
      suggest: {
        title: 'Suggest an improvement',
        subtitle: 'Share an idea to make Riloma better',
        label: 'Your idea',
        placeholder: 'Example: Tell us what would make your experience better',
        action: 'Send idea',
        // Keep the number in sync with MAX_IDEA_LENGTH (pages/help-support/model/limits.ts).
        maxLengthError: 'Your idea must be 500 characters or fewer.',
      },
      feedback: {
        title: 'Share feedback',
        subtitle: 'Tell us about your experience',
        label: 'Your feedback',
        placeholder: 'Example: Tell us what you liked or what could be better',
        action: 'Send feedback',
      },
      cancel: 'Cancel',
      addAttachment: '+Add Attachment',
      removeAttachment: 'Remove',
      attachmentLabel: 'Attach a screenshot or file (optional)',
      // Keep the number in sync with MAX_ATTACHMENT_MB (pages/help-support/model/limits.ts).
      attachmentTooLarge: 'This file is too large. The maximum allowed size is 10 MB.',
      toast: {
        reportSuccess: 'Your report has been submitted.',
        suggestSuccess: 'Your idea has been submitted.',
        feedbackSuccess: 'Thank you for your feedback.',
        error: 'Something went wrong. Please try again.',
      },
    },
  },

  // Dashboard page.
  dashboard: {
    title: 'Dashboard',
    overallReceive: 'Overall, You Will Receive',
    overallPay: 'Overall, You Need To Pay',
    allSettledUp: 'You are all settled up',
    noReceivableOrPayable: 'No receivable or payable',
    viewAll: 'View all',
    tiles: {
      thisMonthSpending: 'This month’s spending',
      thisMonthSettled: 'This month settled / deposited',
      activeGroups: 'Active groups',
      pendingSettlements: 'Pending settlements',
      noSpending: "No Month Spending's Found",
      noSettled: 'No Month settled Recorded',
      noActiveGroups: 'No Active groups Found',
      noPendingSettlements: 'No Pending settlements',
    },
    spendMix: {
      title: 'Spend Mix',
      range: 'Till date',
      emptyTitle: 'No spending data yet',
      emptySubtitle: 'Add expense to see analytics & trends here',
      categories: {
        meals: 'Meals & Dining',
        groceries: 'Groceries',
        cabs: 'Cabs & Rides',
        utilities: 'Utilities',
        snacks: 'Snacks & Drinks',
        entertainment: 'Entertainment',
        other: 'Other',
      },
    },
    needsAttention: {
      title: 'Needs your Attention',
      subtitle: 'Saved, but not finished. Complete or place these expenses',
      total: 'Total:',
      unassigned: 'Unassigned',
      needDetails: 'Need details',
      fixNow: 'Fix now',
    },
    pendingCard: {
      title: 'Pending Settlements',
      empty: 'No Further Pending Settlements Found',
    },
    recentCard: {
      title: 'Recent Group Activities',
      by: 'by',
      empty: 'No Recent Group Activities Found',
    },
  },

  // "New Bill" (Add expense) modal.
  newBill: {
    title: 'New Bill for',
    selectGroup: 'Select group',
    // Shown inside the group dropdown's popover when the account has no groups to pick from yet.
    noGroupsAvailable: 'No groups available',
    skipNote: 'You can skip now and organize later',
    close: 'Close',
    currency: 'Currency',
    amount: 'Amount',
    amountPlaceholder: '0.00',
    exchangeLabel: 'Current exchange value:',
    // Shown under Amount only while the wallet is the payer, e.g. "Wallet Balance: ₹4,894.80".
    walletBalance: 'Wallet Balance:',
    paidBy: 'Paid by:',
    split: 'Split:',
    paidByYou: 'You',
    // Field label flips to "Paid from" when the payer is the wallet (else the default "Paid by:").
    paidFrom: 'Paid from:',
    // Paid-by trigger labels: a lone wallet payer shows "Wallet"; several payers show "Multiple".
    paidByWallet: 'Wallet',
    paidByMultiple: 'Multiple',
    // Split field trigger label — reflects the saved split mode.
    splitEvenly: 'Evenly',
    splitShares: 'Shares',
    splitPercentage: 'Percentage',
    splitAmount: 'Amount',
    whatFor: 'What was this for?',
    descriptionPlaceholder: 'Description (e.g., Team dinner)',
    date: 'Date',
    recurrence: 'Recurrence',
    attachReceipt: 'Attach receipt',
    browseReceipt: 'Click to browse receipt',
    // Shown while a HEIC/HEIF receipt is being converted for its preview.
    receiptPreviewPreparing: 'Preparing preview…',
    // Shown in the Attach Receipt modal while an existing (edit-mode) attachment's bytes are
    // still being fetched — the fetch is lazy (only starts once this modal opens).
    attachmentLoading: 'Loading attachment…',
    // Generic display name (+ extension) for an existing attachment whose original filename this
    // browser never learned — NEVER shows the backend's generated storage id/filename directly.
    attachmentGenericName: 'Receipt',
    viewAttachment: 'View Attachment',
    addBreakdown: 'Add Breakdown',
    clickAddBreakdown: 'Click to add breakdown',
    viewBreakdown: 'View breakdown',
    breakdownStatus: {
      matched: 'Receipt total matched',
      exceeds: 'Exceeds receipt amount',
      remaining: 'Remaining amount',
    },
    // The +/− box beside an "Other" charge in the Itemization modal — a toggle for that row only.
    toggleChargeSign: 'Switch this charge between adding and subtracting',
    note: 'Note (Optional)',
    notePlaceholder: 'Add a quick note for the expense',
    whoCanSee: 'Who can see this expense?',
    visibleToGroup: 'Visible to group',
    privateToYou: 'Private to you',
    addLocation: 'Add a location?',
    // The location chip's label in BOTH states (Figma): before a pick it invites attaching; after
    // a pick it keeps reading "Attach Location" (the selected place's name is NOT shown — the ×
    // remove button beside it is the selected indicator).
    attachLocation: 'Click here to add location',
    locationModal: {
      title: 'Select location',
      searchPlaceholder: 'Search for area, street name...',
      nearbyLocations: 'Nearby locations',
      searchResults: 'Search results',
      selectedLocation: 'Selected location',
      useCurrentLocation: 'Use current location',
      confirmLocation: 'Confirm Location',
      cancel: 'Cancel',
      removeLocation: 'Remove location',
      loading: 'Loading…',
      noResults: 'No results found',
      tryDifferentSearch: 'Try a different search term',
      noneSelected: 'None selected. Search or pick a nearby place.',
      mapNoPreview: 'No preview available',
      locationUnsupported: 'Location is not supported on this device.',
      locationPermissionDenied: 'Location access was denied. Enable it in your browser settings.',
      locationUnavailable: 'Could not fetch your current location. Please try again.',
      mapPreviewAlt: 'Map preview of the selected location',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
    },
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    validation: {
      // Exact copy from the mobile save chain (Quick Add Expense §6 / §11).
      amountOrDescription: 'Please enter Amount or Description or add Attachment.',
      amountInvalid: 'Please enter a valid amount.',
      // Description is mandatory on save (shown inline under the description field).
      descriptionRequired: 'Description is required.',
      selectGroup: 'Please select group',
      currencyRequired: 'Please select a currency.',
      // Shown when Paid-by / Split is tapped before an amount is entered.
      enterAmount: 'Please enter the amount',
      // Mobile parity (`showCommonExpenseErrors`/`groupNoPaidMemebers`): Paid-by tapped on a group with
      // no OTHER member and no wallet — there's genuinely no one to pick as payer.
      noPayerAvailable: 'There is no one to record as the payer yet. Please add a member first.',
      // A recurring expense (group-bill endpoint) requires a description.
      descriptionRequiredRecurring: 'Please enter a description for a recurring expense.',
      // Recurrence can't be combined with a wallet payer (mobile §16.1).
      recurrenceNotAvailableWallet: 'Recurrence expense is not available for wallet.',
      paidByMismatch: "Please manage 'Paid by' entries must equal the expense amount.",
      splitMismatch: "Please manage 'Split by' entries must equal the expense amount.",
      // Currency-conversion guards (Quick Add Expense §6 guards 2/3/6a).
      exchangeRateLoading: 'Fetching latest exchange rate, please wait.',
      convertedTooSmall: 'Bill converted amount too small',
      // `{count}` = number of split participants, `{symbol}` = group-currency symbol.
      convertedTooSmallToSplit:
        'Bill converted amount too small to split among {count} people. Minimum share per person is {symbol}0.01',
      // Breakdown guard (§6 guard 4).
      breakdownTotalMismatch:
        'The breakdown total must match the receipt amount before you can save.',
      itemNameRequired: 'Item name is required',
      // Receipt picker cap — keep in sync with `MAX_RECEIPT_MB` (ReceiptUploadModal).
      receiptTooLarge: 'This file is too large. The maximum allowed size is 10 MB.',
    },
    toast: {
      saved: 'Expense saved',
      saveError: 'Could not save the expense. Please try again.',
    },
    setCurrencyPreference: {
      title: 'Set Currency preference',
      // {currencyCode} = e.g. "USD", {currencySymbol} = e.g. "$", {groupName} = selected group name
      body: 'Set {currencyCode} ({currencySymbol}) as the default currency for new transactions in "{groupName}"?',
      note: "You can change it anytime while adding a transaction. This will not change the group's default currency.",
      // {currencyCode} = e.g. "USD"
      setAs: 'Set as {currencyCode}',
      notNow: 'Not Now',
    },
    splitModal: {
      title: 'Split Expenses',
      clearAll: 'Clear all',
      selectAll: 'Select all',
      paidBy: 'Paid by',
      you: 'You',
      tabs: {
        evenly: 'Split evenly',
        shares: 'Split by shares',
        percentage: 'Split by percentage',
        amount: 'Split by amount',
      },
      // Add-up validation on Save (mobile parity). `{total}` / `{amount}` include the currency symbol.
      validation: {
        noMembers: 'Minimum one user must be selected',
        amountAddMore: 'Amount must add up to {total}. Add {amount} to continue.',
        amountRemove: 'Amount must add up to {total}. Remove {amount} to continue.',
        percentAddMore: 'Percentage must add up to 100%. Add {percent}% to continue.',
        percentRemove: 'Percentage must add up to 100%. Remove {percent}% to continue.',
      },
    },
    splitChoiceModal: {
      title: 'Want to split by line-items?',
      close: 'Close',
      description: {
        text: 'You added item breakdowns. Do you want to assign each item to specific people, or split the full total using your current split preference (evenly)?',
        emphasis: 'assign each item to specific people',
      },
      giveItemsToPeople: 'Give items to people',
      splitTotalEvenly: 'Split total (evenly)',
    },
    paidByModal: {
      title: 'Paid',
      from: 'by',
      transactionAmount: 'Transaction Amount',
      virtualWallet: 'Virtual wallet',
      currentBalance: 'Current Balance:',
      fullPaid: 'Full paid',
      lowBalance: 'Low balance, Choose who paid',
      insufficient: 'Low balance, Choose who paid',
      multiple: 'Paid by multiple members',
      selectMemberForRemaining: 'Please select at least one member who paid remaining balance',
      selectPayer: 'Please select at least one payer',
      // A repeating expense can't be paid from the wallet (mobile §16.1) — tapping the wallet row
      // toasts this, and the row carries the hint.
      walletRecurringToast:
        'Repeating expenses cannot be paid from the wallet. Please choose another way to pay.',
      walletRecurringHint: 'Wallet is not available for recurring transaction',
    },
    categoryModal: {
      title: 'Categories',
      search: 'Search category',
      loading: 'Loading categories…',
      empty: 'No categories found',
      items: {
        meals: {
          name: 'Meals & Dining',
          description: 'Food at restaurants, cafes, take aways, or ordering food together.',
        },
        groceries: {
          name: 'Groceries',
          description: 'Daily grocery shopping like vegetables, milk, fruits, and kitchen items.',
        },
        snacks: {
          name: 'Snacks & Drinks',
          description: 'Tea, coffee, snacks, juices, or small food purchases.',
        },
        liquor: { name: 'Liquor & Alcohol', description: 'Alcoholic drinks and beverages.' },
        rent: {
          name: 'Rent',
          description: 'House rent, room rent, hostel fees, or shared accommodation charges.',
        },
        electricity: {
          name: 'Electricity',
          description: 'Electricity bills or shared power charges.',
        },
      },
    },
    repeat: {
      label: 'Repeat:',
      none: 'None',
      stopRepeat: 'Stop Repeat',
      title: 'Repeat',
      frequency: 'Frequency',
      every: 'Every',
      endRepeat: 'End Repeat',
      earlyReminder: 'Early Reminder',
      frequencies: { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly' },
      endNever: 'Never',
      endOnDate: 'On Date',
      reminders: {
        none: 'None',
        sameDay: 'Same day',
        oneDay: '1 Day before',
        twoDays: '2 Days before',
        oneWeek: '1 Week before',
      },
    },
  },

  // "Settle up" modal (opened from the group detail Settle up button).
  settleUp: {
    title: 'Settle up for',
    close: 'Close',
    yourBalance: 'Your balance',
    receivable: 'Receivable',
    liability: 'Liability',
    settled: 'Settled',
    unsettledTotal: 'Unsettled total',
    pendingPayments: 'Pending payments',
    walletBalance: 'Wallet balance',
    yourShare: 'Your share:',
    pleaseFundWalletImmediately: 'Please fund the wallet immediately !',
    heading: 'Keep pending balances settled',
    description: 'Review pending settlements and record payments.',
    settlementCurrency: 'Settlement currency',
    forYou: 'For you',
    forOthers: 'For group',
    youPay: 'You need to pay',
    youReceive: 'You will receive from',
    tapToRecord: 'Tap to record',
    empty: 'No Pending settlements Available',
    settlementRecorded: 'Payment recorded successfully.',
    settlementUpdated: 'Payment updated successfully.',
    settlementDeleted: 'Payment deleted successfully.',
    settlementDeleteError: 'Could not delete the payment. Please try again.',
    settlementUpdateError: 'Could not update the payment. Please try again.',
    noCurrenciesFound: 'No currencies found',
    rateUnavailable:
      "Couldn't fetch the exchange rate. Showing the amount in the group's home currency.",
  },

  // Group detail (master-detail) view.
  groupDetail: {
    back: 'Back',
    // Toast when the open group no longer exists for this user (deleted by another member, or the
    // user was removed) — the page redirects home.
    groupUnavailable: 'This group is no longer available.',
    groupMember: 'Group member',
    groupMembers: 'Group members',
    settleUp: 'Settle up',
    export: 'Export',
    groupActivity: 'Group Activity',
    closedNote: 'This group is closed. No changes can be made right now.',
    overallReceive: 'Overall, You Will Receive',
    overallPay: 'Overall, You need to pay',
    overallSettled: 'Overall, You Are Settled',
    shared: 'Shared',
    private: 'Private',
    virtualBalance: 'Virtual Balance',
    walletBalance: 'Wallet balance:',
    pleaseFundWalletImmediately: 'Please fund the wallet immediately !',
    yourShare: 'Your share:',
    youPay: 'You need to pay',
    youReceive: 'You will receive from',
    paidBy: 'Paid by',
    paidByYou: 'Paid by You',
    paidByMultipleMembers: 'Paid by Multiple Members',
    paidFromMultipleMembers: 'Paid from multiple members',
    paidFromWallet: 'Paid from wallet',
    paidEntirelyBy: 'Paid entirely by',
    paidEntirelyByYou: 'Paid entirely by you',
    privateExpense: 'Private Expense',
    settlementTo: 'To',
    addedBy: 'Added by',
    depositedBy: 'Deposited by',
    fromWalletTo: 'From wallet to',
    walletBalanceClosure: 'Wallet Balance Closure',
    fundAllocatedTo: 'Fund allocated to',
    by: 'By',
    // Ledger row note for a currency-converted expense, e.g. "(Converted from INR 3,998.82)" — the
    // row's own amount is the ORIGINAL bill currency (matches mobile); this discloses what that
    // comes to in the group's settlement currency.
    convertedFrom: 'Converted from',
    loading: 'Loading expenses…',
    noExpensesTitle: 'No expenses added yet',
    noExpensesHint: 'Start by adding the first expense to this group.',
    rowActions: {
      menuLabel: 'Expense actions',
      viewDetails: 'View Details',
      edit: 'Edit',
      delete: 'Delete',
    },
    badges: {
      liability: 'Liability',
      receivable: 'Receivable',
      private: 'Private',
      excluded: 'Excluded',
      self: 'Self',
      transfer: 'Transfer',
      received: 'Received',
      paid: 'Paid',
      otherPaid: 'Paid',
      walletDeposit: 'Wallet Deposit',
      fundAllocation: 'Fund Allocation',
    },
    // "Group Members" modal, opened from the header's member-avatar strip.
    membersDialog: {
      title: 'Group Members',
      close: 'Close',
      member: 'Member',
      members: 'Members',
      // Member tags (mobile parity): "You (Sagar P)", "Creator & Wallet Manager".
      you: 'You',
      creator: 'Creator',
      walletManager: 'Wallet Manager',
      tagSeparator: ' & ',
    },
    // Recurring-expense line under a ledger row, composed e.g. "Repeats every day" or
    // "Repeats every 12 weeks on Thursday" (the weekday derived from the recurrence start date).
    recurrence: {
      repeatsEvery: 'Repeats every',
      on: 'on',
      day: 'day',
      days: 'days',
      week: 'week',
      weeks: 'weeks',
      month: 'month',
      months: 'months',
      year: 'year',
      years: 'years',
    },
  },

  // Expense Details modal — opened from a ledger row's "View Details" action.
  expenseDetails: {
    title: 'Expense Details',
    close: 'Close',
    notePrefix: 'Note:',
    // Currency-conversion alert, composed e.g. "INR 200.00 converted to USD at 94.29 on 31-Mar 2026".
    convertedTo: 'converted to',
    at: 'at',
    on: 'on',
    // Recurrence alert, composed e.g. "Repeats every month on day 3 | Next date: 3-Jan 2026".
    repeatsEvery: 'Repeats every',
    onDay: 'on day',
    nextDate: 'Next date:',
    summary: 'Expense Summary',
    viewAttachment: 'View attachment',
    receiptPreview: 'Receipt preview',
    closePreview: 'Close preview',
    attachmentLoading: 'Loading receipt…',
    attachmentUnavailable: 'Receipt could not be loaded.',
    // Under a PDF receipt's embedded viewer — opens the same file in the browser's full viewer.
    openInNewTab: 'Open in a new tab',
    paidBy: 'Paid by',
    splitShare: 'Split share',
    wallet: 'Wallet',
    edit: 'Edit',
    delete: 'Delete',
    createdBy: 'Created by',
  },

  // Delete-expense confirmation dialog — opened from a ledger row or the details modal.
  deleteExpense: {
    title: 'Delete expense?',
    close: 'Close',
    body1: 'This expense will be deleted for everyone in this group, not just you.',
    body2: 'You can restore it from Recently Deleted within 60 days.',
    cancel: 'Cancel',
    delete: 'Delete',
    toast: {
      deleted: 'Expense deleted.',
      error: 'Could not delete the expense. Please try again.',
    },
  },

  // Fund Deposit details modal — opened from a WALLET_TOPUP ledger row's "View Details".
  fundDeposit: {
    title: 'Fund Deposit',
    close: 'Close',
    depositedBy: 'Deposited By',
    depositedTo: 'Deposited To',
    walletmanager: 'Wallet Manager',
    date: 'Date',
    createdBy: 'Created by',
    on: 'on',
    viewDetails: 'View Details',
  },

  // Export modal — opened from the group detail Export button.
  exportGroup: {
    title: 'Export for',
    close: 'Close',
    dateRange: 'Date range',
    allTime: 'All time',
    thisMonth: 'This Month',
    customRange: 'Custom Range',
    startDate: 'Start Date',
    endDate: 'End Date',
    categories: 'Categories',
    noCategoriesSelected: 'No categories selected',
    options: {
      onlyPaid: {
        title: 'Only what you paid',
        description: 'Includes your wallet deposits. Excludes private expenses.',
      },
      onlyPrivate: {
        title: 'Only private expenses',
        description: 'Exports only your private entries from this group.',
      },
      wallet: {
        title: 'Include wallet activity',
        description: 'Deposits and wallet spend entries',
      },
      settlements: {
        title: 'Include settlements',
        description: 'Paid to, received from, and settle records.',
      },
    },
    categoryPicker: {
      title: 'Categories',
      add: 'Add',
      cancel: 'Cancel',
    },
    download: 'Download',
    downloading: 'Downloading…',
    toast: {
      downloaded: 'Export downloaded successfully.',
      noData: 'No transactions found for the selected filters',
      selectDates: 'Please select both start and end dates.',
      error: 'Failed to download export. Please try again.',
    },
  },

  // Plan usage restriction modals — shown when a free-tier limit is reached or a Pro-only
  // feature is accessed. Driven by planUsageManager.check() → PlanUsageLimitModal.
  planUsage: {
    notNow: 'Not now',
    upgradeToPro: 'Upgrade to Pro',
    // {days} is replaced at render time with the numeric days-until-reset.
    resetsIn: 'Resets in {days} days.',
    currency: {
      title: 'Currency change is a Pro feature',
      body: 'Add transactions in different currencies.',
      description: 'Upgrade to Pro to change currency while adding transactions.',
    },
    export: {
      title: 'Export limit reached',
      // {count} = number already used this month (singular vs plural)
      bodySingular: "You've used your {count} free export this month",
      bodyPlural: "You've used your {count} free exports this month",
      description: 'You can export again next month, or upgrade to Pro for unlimited exports.',
    },
    attachment: {
      title: 'Attachment limit reached',
      // {count} = number added this week
      body: "You've added {count} attachments this week",
      description: 'You can add more next week, or upgrade to Pro for unlimited attachments.',
    },
    proFeature: {
      title: 'Pro feature',
      body: 'This feature is available on the Pro plan.',
      description: 'Upgrade to Pro to unlock this and other advanced features.',
    },
  },

  // "My Groups" rail (dashboard right column).
  myGroups: {
    title: 'My Groups',
    tabs: { active: 'Active', archived: 'Archived', closed: 'Closed', all: 'All' },
    receivable: 'Receivable',
    liability: 'Liability',
    payTo: 'Pay to',
    receiveFrom: 'Receive from',
    waiting: 'Waiting for the first expense',
    empty: 'No groups yet',
    member: 'member',
    members: 'members',
  },

  // Free Pro / Beta Program Invitation.
  invite: {
    differentAccountModal: {
      title: 'Beta Program Invitation',
      description:
        'You’ve been invited to join the Riloma Beta Program with a different account. Do you want to log out of your current account and continue with the invited account?',
      stayLoggedIn: 'No, Stay Logged In',
      logoutAndContinue: 'Yes, Log out & Continue',
    },
  },
} as const;

export type Messages = typeof en;
